import { useEffect, useRef, useState } from 'react';

import { almacenToken } from '../lib/api';
import { baseApi } from '../lib/servidor';

/**
 * Lo que la cámara del kiosco ve en la charola, en vivo.
 *
 * No es un `<img>` apuntando a la URL. El endpoint exige sesión y un `src`
 * normal no manda la cabecera `Authorization`; la alternativa habitual —colgar
 * el token de la URL— lo dejaría escrito en la bitácora de nginx, que es
 * exactamente lo que se evitó en el flujo del guardia. Así que cada cuadro se
 * pide con `fetch` y se pinta desde un blob.
 *
 * La contrapartida es que hay que devolver cada blob a mano: a dos cuadros por
 * segundo, olvidarse de `revokeObjectURL` deja el navegador de la caseta
 * comiéndose la memoria durante todo un turno.
 */

/** Cada cuánto se pide un cuadro. El kiosco manda ~2,5 por segundo. */
const CADENCIA_MS = 400;

export function VistaEnVivo({ accesoId }: { accesoId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [hayImagen, setHayImagen] = useState(false);
  const anterior = useRef<string | null>(null);

  useEffect(() => {
    let vivo = true;
    let reloj: ReturnType<typeof setTimeout> | null = null;

    async function traer(): Promise<void> {
      try {
        const r = await fetch(`${baseApi()}/api/accesos/${accesoId}/vista-previa`, {
          headers: { Authorization: `Bearer ${almacenToken.leer() ?? ''}` },
          cache: 'no-store',
        });
        if (!vivo) return;

        // 204 = el kiosco todavía no manda nada, o dejó de mandar. Se conserva
        // el último cuadro en pantalla y se avisa aparte: parpadear a negro
        // entre cuadros haría imposible mirar nada.
        if (r.status === 204) {
          setHayImagen(false);
        } else if (r.ok) {
          const u = URL.createObjectURL(await r.blob());
          if (!vivo) {
            URL.revokeObjectURL(u);
            return;
          }
          if (anterior.current) URL.revokeObjectURL(anterior.current);
          anterior.current = u;
          setUrl(u);
          setHayImagen(true);
        }
      } catch {
        // Un cuadro perdido no es nada: el siguiente llega en 400 ms.
      } finally {
        // Se reprograma al **terminar** y no con un intervalo fijo: si la red
        // va lenta, un intervalo encolaría peticiones que se pisan unas a
        // otras y el retraso crecería sin parar.
        if (vivo) reloj = setTimeout(() => void traer(), CADENCIA_MS);
      }
    }

    void traer();

    return () => {
      vivo = false;
      if (reloj) clearTimeout(reloj);
      if (anterior.current) URL.revokeObjectURL(anterior.current);
      anterior.current = null;
    };
  }, [accesoId]);

  return (
    <figure className="relative overflow-hidden rounded-xl border border-outline-variant bg-black">
      {url ? (
        <img
          src={url}
          alt="Charola del kiosco en vivo"
          className="h-64 w-full object-contain"
          // El navegador no debe animar el cambio de imagen ni reservar hueco:
          // los cuadros llegan seguidos y cualquier transición se ve como un
          // parpadeo.
          draggable={false}
        />
      ) : (
        <div className="flex h-64 items-center justify-center text-body-md text-on-surface-variant">
          Esperando la cámara del kiosco…
        </div>
      )}

      {url && !hayImagen && (
        <span className="absolute inset-x-0 bottom-0 bg-error px-3 py-1.5 text-center text-label-bold uppercase tracking-wider text-on-error">
          Imagen congelada — el kiosco dejó de transmitir
        </span>
      )}
    </figure>
  );
}
